import React from 'react';
import { useTranslation } from 'react-i18next';

const STATUS_COLOR = {
  pending:   '#d97706',
  running:   '#1f58a4',
  completed: '#059669',
  failed:    '#dc2626',
};
const STATUS_KEY = {
  pending:   'researchCard.statusPending',
  running:   'researchCard.statusRunning',
  completed: 'researchCard.statusCompleted',
  failed:    'researchCard.statusFailed',
};

function Stars({ n }) {
  return (
    <span style={{ fontSize: 14, letterSpacing: 1 }}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} style={{ color: i < n ? '#d97706' : 'var(--border)' }}>★</span>
      ))}
    </span>
  );
}

function Badge({ label, color }) {
  return (
    <span style={{
      background: color + '15', color,
      border: `1px solid ${color}35`,
      borderRadius: 6, padding: '2px 8px',
      fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
    }}>
      {label}
    </span>
  );
}

export default function ResearchCard({ doc, onClick, onReResearch }) {
  const { t, i18n } = useTranslation();
  const statusColor = STATUS_COLOR[doc.status] || '#6b8dad';

  // Re-research only when the report is completed and older than 7 days
  const ageDays = doc.completedAt ? (Date.now() - new Date(doc.completedAt).getTime()) / 86400000 : null;
  const canReResearch = onReResearch && doc.status === 'completed' && ageDays != null && ageDays >= 7;

  return (
    <div style={styles.card} onClick={() => doc.status === 'completed' && onClick(doc.slug)}>
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <span style={styles.name}>{doc.name}</span>
          {doc.ticker && <span style={styles.ticker}>{doc.ticker}</span>}
        </div>
        {canReResearch && (
          <button
            style={styles.reBtn}
            onClick={e => { e.stopPropagation(); onReResearch(doc.slug); }}
            title={t('researchCard.reResearchTitle')}
          >
            🔄 {t('researchCard.reResearch')}
          </button>
        )}
      </div>

      <div style={styles.statusRow}>
        <span style={{ ...styles.statusDot, background: statusColor }} />
        <span style={{ color: statusColor, fontSize: 12, fontWeight: 600 }}>{t(STATUS_KEY[doc.status])}</span>
        {doc.status === 'running' && <span style={{ ...styles.pulse, background: statusColor }} />}
      </div>

      {doc.status === 'completed' && (
        <div style={styles.meta}>
          {doc.category && <Badge label={doc.category} color="#0891b2" />}
          {doc.teamRating && (
            <Badge label={t('researchCard.teamLabel', { rating: doc.teamRating })}
              color={doc.teamRating === 'Strong' ? '#059669' : doc.teamRating === 'Average' ? '#d97706' : '#dc2626'} />
          )}
          {doc.unlockRisk && (
            <Badge label={t('researchCard.unlockLabel', { risk: doc.unlockRisk })}
              color={doc.unlockRisk === 'Low' ? '#059669' : doc.unlockRisk === 'Medium' ? '#d97706' : '#dc2626'} />
          )}
          {doc.fdv && <Badge label={`FDV: ${doc.fdv}`} color="#6b8dad" />}
          {doc.isCached === true  && <Badge label={`💾 ${t('researchCard.cache')} · ${doc.cacheAgeDays}d`} color="#059669" />}
          {doc.isCached === false && doc.status === 'completed' && <Badge label={`⚠️ ${t('researchCard.stale')}`} color="#d97706" />}
        </div>
      )}

      <div style={styles.footer}>
        {doc.verdict ? <Stars n={doc.verdict} /> : <span />}
        <span style={styles.date}>
          {new Date(doc.createdAt).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </span>
      </div>

      {doc.status === 'completed' && (
        <div className="viewHint" style={styles.viewHint}>{t('researchCard.viewReport')} →</div>
      )}
    </div>
  );
}

const styles = {
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12, padding: '16px 18px',
    cursor: 'pointer', position: 'relative', overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(31,88,164,0.06)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  titleRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  name: { fontSize: 15, fontWeight: 700, color: 'var(--text)' },
  ticker: {
    fontSize: 12, fontWeight: 700, color: '#0891b2',
    background: 'rgba(8,145,178,0.1)', padding: '1px 7px',
    borderRadius: 5, border: '1px solid rgba(8,145,178,0.25)',
  },
  reBtn: {
    background: 'rgba(217,119,6,0.1)', border: '1px solid rgba(217,119,6,0.35)',
    color: '#b45309', borderRadius: 7, padding: '4px 10px', flexShrink: 0,
    cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
  },
  statusRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 },
  statusDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  pulse: { width: 7, height: 7, borderRadius: '50%', animation: 'pulse 1.4s ease-in-out infinite' },
  meta: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  date: { fontSize: 11, color: 'var(--text-dim)' },
  viewHint: {
    position: 'absolute', bottom: 10, right: 14,
    fontSize: 11, color: 'var(--accent)', opacity: 0, transition: 'opacity 0.2s',
  },
};
